/** Datadog RUM init and helpers. RUM is off unless credentials are configured. */

import { datadogRum, RumEvent } from "@datadog/browser-rum";

import { captureMessage } from "./sentry";

/**
 * `uninitialized` until `initializeDatadog` runs, then `enabled` or, without
 * credentials, `disabled`. The helpers below tell the two apart: RUM being off
 * is expected (local dev), a call before bootstrap is a bug.
 */
type DatadogState = "uninitialized" | "disabled" | "enabled";
let datadogState: DatadogState = "uninitialized";
let useBeforeInitReported = false;

const SERVICE_NAME = "b2b-web-enrollment";

// Noom access codes are 8 uppercase alphanumerics (e.g. W7EZGJTZ) and appear as
// path segments in several API URLs (`/outcomes/v1/userprofiles/{accessCode}`).
// API route segments are lowercase / camelCase / `v1`, so this cannot match them.
const ACCESS_CODE = /^[A-Z0-9]{8}$/;

/**
 * Identifiers whose format is not predictable (UPIDs are word-based activation
 * codes like `tocold49`, passcodes are user-entered) are recognised by the
 * segment that precedes them instead:
 *   /programs/upid/{upid}:getEnrollmentInformation
 *   /programs/upid/validate/{upid}
 *   /batches/-/batchPasscodes/{passcode}:generateUpidForPartner
 */
function placeholderForSegment(
  segments: string[],
  index: number,
): string | null {
  const [value] = segments[index].split(":", 1);
  if (value === "") return null;
  if (ACCESS_CODE.test(value)) return "{accessCode}";

  const parent = segments[index - 1];
  if (parent === "batchPasscodes") return "{passcode}";
  if (parent === "upid" && value !== "validate") return "{upid}";
  if (parent === "validate" && segments[index - 2] === "upid") return "{upid}";
  return null;
}

// Keeps a Google-style custom-method suffix (`{accessCode}:enrollB2bUser`) so
// the endpoint is still identifiable after the identifier is masked.
function redactSegment(segments: string[], index: number): string {
  const segment = segments[index];
  const placeholder = placeholderForSegment(segments, index);
  if (!placeholder) return segment;
  const colon = segment.indexOf(":");
  return colon === -1 ? placeholder : placeholder + segment.slice(colon);
}

/**
 * Strips PII from a URL before it reaches Datadog. Keeps origin and path so
 * views and failed requests are still identifiable; drops the query string and
 * fragment (landing URLs carry `name`, `email`, `ref_email`, `utm_term`,
 * `utm_content`, and `urlParamsPii.init()` only scrubs them later, at
 * conversion) and masks identifier path segments (access code, UPID,
 * passcode). Malformed input is replaced rather than passed through.
 */
export function redactUrl(rawUrl: string): string {
  try {
    const url = new URL(rawUrl);
    const segments = url.pathname.split("/");
    const path = segments.map((_, i) => redactSegment(segments, i)).join("/");
    return `${url.origin}${path}`;
  } catch {
    return "redacted:";
  }
}

const EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const HTTP_URL = /https?:\/\/[^\s"'`<>()]+/g;

/**
 * Scrubs free text the SDK assembles itself. Thrown non-`Error` values and
 * `console.error` arguments are JSON-stringified into `error.message`, so an
 * API response or form object logged by mistake would otherwise ship verbatim.
 * Replaces emails and runs every embedded URL through `redactUrl`.
 */
export function redactText(text: string): string {
  return text
    .replace(HTTP_URL, (url) => redactUrl(url))
    .replace(EMAIL, "[email]");
}

/** RUM `beforeSend`: redacts every URL and free-text field the SDK captures. */
export function beforeSend(event: RumEvent): boolean {
  /* eslint-disable no-param-reassign -- the SDK requires in-place mutation */
  event.view.url = redactUrl(event.view.url);
  if (event.view.referrer) {
    event.view.referrer = redactUrl(event.view.referrer);
  }
  if (event.type === "resource") {
    event.resource.url = redactUrl(event.resource.url);
  }
  if (event.type === "error") {
    if (event.error.message) {
      event.error.message = redactText(event.error.message);
    }
    if (event.error.stack) {
      event.error.stack = redactText(event.error.stack);
    }
    event.error.causes?.forEach((cause) => {
      if (cause.message) cause.message = redactText(cause.message);
      if (cause.stack) cause.stack = redactText(cause.stack);
    });
    if (event.error.resource) {
      event.error.resource.url = redactUrl(event.error.resource.url);
    }
  }
  /* eslint-enable no-param-reassign */
  return true;
}

// Noom's Datadog `env` tag convention (e.g. b2b-saml-enrollment APM uses `env:prod`).
const DATADOG_ENV: Record<NoomEnvironment, string> = {
  production: "prod",
  test: "test",
  development: "dev",
};

function datadogEnv(): string {
  return (
    process.env.HIVE_DATADOG_ENVIRONMENT ||
    DATADOG_ENV[window.ENV.NOOM_ENVIRONMENT]
  );
}

// `leadSource` is a channel label (`sso`, `buyflow`, ...), never PII. Anything
// that does not look like one is dropped so a crafted URL cannot smuggle text in.
const LEAD_SOURCE_REGEX = /^[a-z0-9_-]{1,32}$/i;

/**
 * The channel the landing URL arrived from, or `undefined`. `redactUrl` drops
 * the query string from `view.url`, so `@view.url_query` is empty in Datadog;
 * the SSO landing monitors read `@context.leadSource` instead.
 */
export function leadSourceFromSearch(search: string): string | undefined {
  const leadSource = new URLSearchParams(search).get("leadSource");
  return leadSource && LEAD_SOURCE_REGEX.test(leadSource)
    ? leadSource
    : undefined;
}

/**
 * Initializes Datadog RUM once. Without credentials RUM stays off; in
 * production that is reported to Sentry, since a dropped Amplify var would
 * otherwise only show up as RUM data quietly stopping. Sentry's `beforeSend`
 * discards dev and test events anyway, and RUM is off by default locally.
 */
export function initializeDatadog(): void {
  if (datadogState !== "uninitialized") return;

  const applicationId = process.env.HIVE_DATADOG_APPLICATION_ID;
  const clientToken = process.env.HIVE_DATADOG_CLIENT_TOKEN;
  if (!applicationId || !clientToken) {
    datadogState = "disabled";
    if (window.ENV.NOOM_ENVIRONMENT === "production") {
      captureMessage(
        "Datadog RUM is off: HIVE_DATADOG_APPLICATION_ID / HIVE_DATADOG_CLIENT_TOKEN not set at build time",
        "error",
      );
    }
    return;
  }

  datadogState = "enabled";

  datadogRum.init({
    applicationId,
    clientToken,
    site: "datadoghq.com",
    service: SERVICE_NAME,
    env: datadogEnv(),
    version: process.env.HIVE_SHA || "dev",
    // Standard Noom RUM config for PHI-facing apps: sample every session, no
    // replay, mask by default, no auto click actions. Keep these in step with the
    // other RUM apps rather than tuning them per app.
    sessionSampleRate: 100,
    // Session replay is off. Triage collects DOB, name, email and eligibility IDs,
    // so a replay would record PHI. NEVER enable this in production. If it is
    // ever needed for debugging, gate it on env (dev/test only) and never call
    // `datadogRum.startSessionReplayRecording()` unconditionally.
    sessionReplaySampleRate: 0,
    // PHI: auto-named click actions take their name from the clicked element's
    // text, and `defaultPrivacyLevel: "mask"` does not cover action names. Triage
    // answers (conditions, DOB, program choices) would land in `@action.name`.
    // Use `trackDatadogAction` for explicit, safely named actions instead.
    trackUserInteractions: false,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask",
    // `mask` covers DOM text, not URLs. See `redactUrl`.
    beforeSend,
  });

  // Captured once from the first URL of the page load. The SAML redirect from
  // b2b-saml-enrollment lands on `/employer/{slug}?leadSource=sso&...`, so every
  // event in an SSO session is tagged and the landing monitors can find them.
  const leadSource = leadSourceFromSearch(window.location.search);
  if (leadSource) {
    datadogRum.setGlobalContextProperty("leadSource", leadSource);
  }
}

/**
 * Whether a helper may forward to the SDK. With RUM deliberately off the
 * helpers are quiet no-ops. A call before `initializeDatadog` means
 * instrumentation ran ahead of `bootstrap()` and the event is lost; that is a
 * bug, so it is reported once per page load instead of dropped silently.
 */
function canSend(caller: string): boolean {
  if (datadogState === "uninitialized" && !useBeforeInitReported) {
    useBeforeInitReported = true;
    captureMessage(
      `${caller} called before initializeDatadog(); the RUM event was dropped`,
      "error",
    );
  }
  return datadogState === "enabled";
}

/** Records a custom RUM action. No-op when RUM is off. */
export function trackDatadogAction(
  name: string,
  context?: Record<string, unknown>,
): void {
  if (!canSend("trackDatadogAction")) return;
  datadogRum.addAction(name, context);
}

/** Records a RUM error. No-op when RUM is off. */
export function trackDatadogError(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  if (!canSend("trackDatadogError")) return;
  datadogRum.addError(error, context);
}

/** Adds a global context property to all RUM events. No-op when RUM is off. */
export function addDatadogContext(key: string, value: unknown): void {
  if (!canSend("addDatadogContext")) return;
  datadogRum.setGlobalContextProperty(key, value);
}

/**
 * Records a duration vital that ended now. `durationMs` is in milliseconds;
 * Datadog stores `@vital.duration` in nanoseconds (`durationMs * 1e6`), so a
 * 5 s monitor threshold is `5000000000`. No-op when RUM is off.
 */
export function recordDurationVital(
  name: string,
  durationMs: number,
  context: Record<string, unknown>,
): void {
  if (!canSend("recordDurationVital")) return;
  const duration = Math.max(0, durationMs);
  datadogRum.addDurationVital(name, {
    startTime: Date.now() - duration,
    duration,
    context,
  });
}

/** Test-only: resets the init state between test cases. */
export function resetDatadogForTesting(): void {
  datadogState = "uninitialized";
  useBeforeInitReported = false;
}
